import { useEffect, useState } from "react";
import type { WdFile } from "@edge/shared";
import { ApiError, fetchDealWorkdriveFileBlob } from "../../api";

interface Props {
  dealId: string;
  file: WdFile;
  onClose: () => void;
}

type Kind = "pdf" | "image" | "unsupported";

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]);

function kindOf(ext: string): Kind {
  const e = ext.toLowerCase();
  if (e === "pdf") return "pdf";
  if (IMAGE_EXTS.has(e)) return "image";
  // Office (doc/docx/xls/xlsx/ppt/pptx) + anything else can't render natively
  // in a browser modal — fall back to download / open-in-WorkDrive.
  return "unsupported";
}

/**
 * WD-2.1 — in-Edge file preview. PDFs render in an <iframe>, images in an <img>;
 * Office and other types can't render natively, so they show a fallback panel
 * with Download + Open-in-WorkDrive. Bytes come through the authenticated proxy
 * (fetchDealWorkdriveFileBlob) as a blob: URL, revoked on unmount.
 */
export function WorkDrivePreviewModal({ dealId, file, onClose }: Props) {
  const kind = kindOf(file.ext);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(kind !== "unsupported");
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Esc closes.
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Pull the blob for previewable kinds; unsupported types skip the fetch.
  useEffect(() => {
    if (kind === "unsupported") return;
    let revoked = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    fetchDealWorkdriveFileBlob(dealId, file.id)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((e) => {
        if (revoked) return;
        setError(
          e instanceof ApiError && e.code === "file_too_large"
            ? "File is over the 25 MB preview limit — open it in WorkDrive."
            : e instanceof ApiError
              ? `${e.code} (HTTP ${e.status})`
              : "Couldn’t load this file.",
        );
      })
      .finally(() => !revoked && setLoading(false));
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [dealId, file.id, kind]);

  async function download() {
    setDownloading(true);
    try {
      const blob = await fetchDealWorkdriveFileBlob(dealId, file.id, true);
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch {
      setError("Download failed — try Open in WorkDrive.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      className="write-confirm-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="wd-preview-card" onClick={(e) => e.stopPropagation()}>
        <div className="wd-preview-head">
          <span className="wd-preview-name" title={file.name}>
            {file.name}
          </span>
          <div className="wd-preview-actions">
            <button
              className="wd-preview-btn"
              onClick={() => void download()}
              disabled={downloading}
            >
              {downloading ? "…" : "↓ Download"}
            </button>
            <a
              className="wd-preview-btn"
              href={file.permalink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in WorkDrive ↗
            </a>
            <button className="wd-preview-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="wd-preview-body">
          {error ? (
            <div className="wd-preview-msg">⚠ {error}</div>
          ) : kind === "unsupported" ? (
            <div className="wd-preview-msg">
              <p>
                <b>{file.ext ? file.ext.toUpperCase() : "This file type"}</b> can’t
                preview inside Edge.
              </p>
              <p className="wd-preview-sub">
                Use <b>Download</b> to save it, or <b>Open in WorkDrive</b> to view
                it there.
              </p>
            </div>
          ) : loading ? (
            <div className="wd-preview-msg">
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-label">LOADING PREVIEW</span>
            </div>
          ) : url && kind === "pdf" ? (
            <iframe className="wd-preview-frame" src={url} title={file.name} />
          ) : url && kind === "image" ? (
            <img className="wd-preview-img" src={url} alt={file.name} />
          ) : null}
        </div>
      </div>
    </div>
  );
}
